import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Button,
  CircularProgress,
} from '@material-ui/core';
import {
  ArrowForwardIos,
  BuildOutlined,
  CheckCircleOutline,
  PublishOutlined,
  Sync,
} from '@material-ui/icons';
import clsx from 'clsx';
import { useLocalizedMessage } from '../../util/useLocalizedMessage';
import { useRuntimeIndexing } from '../catalog-sync/useRuntimeIndexing';
import { useBundleRebuild } from '../home/components/Diagnostics/useBundleRebuild';
import PublishProgressDialog from '../home/components/PublishProgressDialog';
import { useOperationDialogVisibility } from '../home/components/PublishProgressDialog/useOperationDialogVisibility';
import {
  RUNTIME_OPERATION_STATE_EVENT,
} from '../home/livePublishEvents';
import { isIndexingActive } from '../catalog-sync/indexingRuntimeUtils';
import { useCloudDeploymentStatus } from './useCloudDeploymentStatus';
import {
  subscribeToIndexingRequested,
  subscribeToIndexingRequestFailed,
} from './indexingRequestEvents';
import styles from './CloudOperationsPanel.module.scss';

function useSharedRuntimeOperation() {
  const [operation, setOperation] = useState({ operation: null, status: null, error: null });
  useEffect(() => {
    const handleState = event => setOperation(event.detail || {});
    window.addEventListener(RUNTIME_OPERATION_STATE_EVENT, handleState);
    return () => window.removeEventListener(RUNTIME_OPERATION_STATE_EVENT, handleState);
  }, []);
  return operation;
}

function useOptimisticIndexing(indexingRunning) {
  const [requestedAt, setRequestedAt] = useState(null);
  useEffect(() => {
    const unsubscribeRequested = subscribeToIndexingRequested((detail) => {
      setRequestedAt(detail.requestedAt || Date.now());
    });
    const unsubscribeFailed = subscribeToIndexingRequestFailed(() => setRequestedAt(null));
    return () => {
      unsubscribeRequested();
      unsubscribeFailed();
    };
  }, []);

  useEffect(() => {
    if (indexingRunning) return undefined;
    if (!requestedAt) return undefined;
    // Cloud cannot provide trustworthy progress. Keep the causal signal long
    // enough for status polling to observe the queued job, then retire it.
    const timeoutId = window.setTimeout(() => setRequestedAt(null), 45000);
    return () => window.clearTimeout(timeoutId);
  }, [indexingRunning, requestedAt]);

  return Boolean(indexingRunning || requestedAt);
}

function releasePresentation(status, localizeMessage) {
  switch (status?.state) {
    case 'managed':
      // A server-managed legacy bundle is a normal steady state, not an
      // operation that needs permanent attention in Home or the sidebar.
      return null;
    case 'not_configured':
      return {
        icon: <BuildOutlined />,
        tone: 'warning',
        label: localizeMessage('cloud.release.notConfigured.title'),
        description: localizeMessage('cloud.release.notConfigured.description'),
        actionable: true,
        publishable: false,
      };
    case 'not_published':
    case 'unverified':
      return {
        icon: <PublishOutlined />,
        tone: 'warning',
        label: localizeMessage('cloud.release.notPublished.title'),
        description: localizeMessage('cloud.release.notPublished.description'),
        actionable: true,
        publishable: status?.previewArtifactVerified === true,
      };
    case 'unpublished_changes':
      return {
        icon: <PublishOutlined />,
        tone: 'warning',
        label: localizeMessage('cloud.release.changes.title'),
        description: localizeMessage('cloud.release.changes.description'),
        actionable: true,
        publishable: status?.previewArtifactVerified === true,
      };
    case 'publishing':
      return {
        icon: <CircularProgress size={19} color="inherit" />,
        tone: 'info',
        label: localizeMessage('cloud.release.publishing.title'),
        description: localizeMessage('cloud.release.publishing.description'),
        actionable: false,
      };
    case 'publish_failed':
      return {
        icon: <PublishOutlined />,
        tone: 'warning',
        label: localizeMessage('cloud.release.failed.title'),
        description: status?.lastPublishError
          || localizeMessage('cloud.release.failed.description'),
        actionable: true,
        publishable: status?.previewArtifactVerified === true,
      };
    case 'published':
      return {
        icon: <CheckCircleOutline />,
        tone: 'success',
        label: localizeMessage('cloud.release.published.title'),
        description: localizeMessage('cloud.release.published.description'),
        actionable: false,
      };
    default:
      return null;
  }
}

export default function CloudOperationsPanel({ variant }) {
  const history = useHistory();
  const localizeMessage = useLocalizedMessage();
  const currentShop = useSelector(state => state.app?.currentShop);
  const deployment = useCloudDeploymentStatus({ shopId: currentShop });
  const indexing = useRuntimeIndexing({ historyLimit: 5, enabled: variant !== 'sidebar' });
  const publishBuild = useBundleRebuild();
  const sharedOperation = useSharedRuntimeOperation();
  const publishDialog = useOperationDialogVisibility(publishBuild.status);
  const shopId = deployment.shopId;
  const indexingRunning = isIndexingActive(indexing.snapshot.current);
  const showIndexing = useOptimisticIndexing(indexingRunning);
  const displayIndexing = variant !== 'sidebar' && showIndexing;
  const publishing = sharedOperation.operation === 'production'
    && ['queued', 'running'].includes(sharedOperation.status);
  const presentation = useMemo(() => {
    if (publishing) {
      return {
        icon: <PublishOutlined />,
        tone: 'info',
        label: localizeMessage('cloud.release.publishing.title'),
        description: localizeMessage('cloud.release.publishing.description'),
        actionable: false,
      };
    }
    return releasePresentation(deployment.status, localizeMessage);
  }, [deployment.status, localizeMessage, publishing]);

  const openInterface = useCallback(() => {
    history.push(`/app/${shopId}/search/settings/v2`);
  }, [history, shopId]);

  const publish = useCallback(() => {
    publishBuild.reset();
    publishDialog.begin();
    Promise.resolve(publishBuild.startBuildAndPromote(shopId)).catch(() => {});
  }, [publishBuild, publishDialog, shopId]);

  if (variant === 'sidebar') {
    const showRelease = presentation
      && (publishing || deployment.status?.state !== 'published');
    if (!showRelease) return null;
    return (
      <div className={styles.sidebarRoot} data-testid="cloud-operation-center">
        {showRelease && (
          <button
            type="button"
            className={styles.sidebarItem}
            onClick={presentation.publishable ? publish : openInterface}
            disabled={publishing || deployment.status?.state === 'managed'}
          >
            <span className={styles.sidebarIcon}>
              {publishing ? <CircularProgress size={17} color="inherit" /> : presentation.icon}
            </span>
            <span className={styles.sidebarText}>{presentation.label}</span>
            {!publishing && deployment.status?.state !== 'managed' && (
              <ArrowForwardIos className={styles.sidebarChevron} />
            )}
          </button>
        )}
        <PublishProgressDialog
          open={publishDialog.open}
          operation={publishBuild.operation || 'production'}
          status={publishBuild.status}
          error={publishBuild.error}
          onRetry={publish}
          onClose={publishDialog.close}
          localizeMessage={localizeMessage}
        />
      </div>
    );
  }

  const showRelease = presentation
    && (publishing || deployment.status?.state !== 'published');
  const showStatusError = deployment.error && !deployment.status;

  // A healthy or still-loading release is the default, not an operation that
  // needs the user's attention. Keep Home quiet until action is actually due.
  if (!showRelease && !displayIndexing && !showStatusError) {
    return null;
  }

  return (
    <section className={styles.homeCard} data-testid="cloud-release-status">
      <div className={styles.homeAccent} />
      <div className={styles.homeContent}>
        <h2 className={styles.homeTitle}>{localizeMessage('cloud.release.title')}</h2>
        {showRelease && (
          <div className={styles.statusRow}>
            <span className={clsx(
              styles.statusIcon,
              presentation.tone === 'warning' && styles.statusIconWarning,
              presentation.tone === 'info' && styles.statusIconInfo,
            )}
            >
              {publishing ? <CircularProgress size={19} color="inherit" /> : presentation.icon}
            </span>
            <span className={styles.statusCopy}>
              <span className={styles.statusLabel}>{presentation.label}</span>
              <span className={styles.statusDescription}>{presentation.description}</span>
            </span>
          </div>
        )}
        {showRelease && presentation?.actionable && !publishing && (
          <div className={styles.actions}>
            <Button size="small" variant="outlined" color="primary" onClick={openInterface}>
              {localizeMessage('cloud.release.review')}
            </Button>
            {presentation.publishable && (
              <Button size="small" variant="contained" color="primary" onClick={publish}>
                {localizeMessage('cloud.release.publish')}
              </Button>
            )}
          </div>
        )}
        {displayIndexing && (
          <div className={styles.statusRow}>
            <span className={clsx(styles.statusIcon, styles.statusIconInfo)}>
              <Sync />
            </span>
            <span className={styles.statusCopy}>
              <span className={styles.statusLabel}>{localizeMessage('cloud.operation.indexing')}</span>
              <span className={styles.statusDescription}>{localizeMessage('cloud.operation.indexing.description')}</span>
            </span>
          </div>
        )}
        {deployment.error && !deployment.status && (
          <p className={styles.errorText}>{localizeMessage('cloud.release.error')}</p>
        )}
      </div>
      <PublishProgressDialog
        open={publishDialog.open}
        operation={publishBuild.operation || 'production'}
        status={publishBuild.status}
        error={publishBuild.error}
        onRetry={publish}
        onClose={publishDialog.close}
        localizeMessage={localizeMessage}
      />
    </section>
  );
}

CloudOperationsPanel.propTypes = {
  variant: PropTypes.oneOf(['home', 'sidebar']),
};

CloudOperationsPanel.defaultProps = {
  variant: 'home',
};
